import React, { useEffect, useState } from "react";
import {
  useSaveResourcesMutation,
  useUpdateResourcesMutation,
} from "../../store/resourcesApi";
import { toast } from "sonner";

const AdminAddResources = ({ editData, setEdit }) => {
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    actionType: "",
    features: [{ title: "", description: "", url: "" }],
  });

  const { item } = editData;

  useEffect(() => {
    if (editData.isEdit && item) {
      setFormData({
        name: item.name,
        title: item.title,
        description: item.description,
        actionType: item.actionType,
        features: item.features || [{ title: "", description: "", url: "" }],
      });
    }
  }, [editData, item]);

  const [saveResources, { isLoading }] = useSaveResourcesMutation();
  const [updateResources] = useUpdateResourcesMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFeatureChange = (index, e) => {
    const { name, value } = e.target;
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = { ...updatedFeatures[index], [name]: value };
    setFormData({ ...formData, features: updatedFeatures });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, { title: "", description: "", url: "" }],
    });
  };

  const removeFeature = (index) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures.splice(index, 1);
    setFormData({ ...formData, features: updatedFeatures });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editData.isEdit) {
        await updateResources({ id: item._id, data: formData }).unwrap();

        // This condition is redundant because you already checked it above
        if (editData.isEdit) {
          setEdit({
            isEdit: false,
            item: {},
          });
        }

        toast.success("Resource updated successfully!");
      } else {
        await saveResources(formData).unwrap();
        toast.success("Resource saved successfully!");
      }

      // Reset form
      setFormData({
        name: "",
        title: "",
        description: "",
        actionType: "",
        features: [{ title: "", description: "", url: "" }],
      });
    } catch (error) {
      console.error("Failed to save resource:", error);
      toast.error("Failed to save resource!");
    }
  };

  return (
    <div className="bg-white dark:bg-zinc-800 shadow-md dark:text-white p-8 rounded-lg w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Name
          </label>
          <input
            id="name"
            type="text"
            name="name"
            placeholder="Enter Resource name"
            value={formData.name}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
            required
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Title
          </label>
          <input
            id="title"
            type="text"
            name="title"
            placeholder="Enter resource title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
            required
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Description
          </label>
          <textarea
            id="description"
            name="description"
            placeholder="Enter resource description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Action Type
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="actionType"
                value="View"
                checked={formData.actionType === "View"}
                onChange={handleChange}
                className="text-red-600 focus:ring-red-500"
                required
              />
              <span>View</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                name="actionType"
                value="Download"
                checked={formData.actionType === "Download"}
                onChange={handleChange}
                className="text-red-600 focus:ring-red-500"
              />
              <span>Download</span>
            </label>
          </div>
        </div>

        <div className="dark:bg-zinc-700 bg-blue-50 p-4 rounded-md">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">View Features</h2>
            <button
              type="button"
              onClick={addFeature}
              className="bg-blue-500 dark:bg-white   hover:bg-blue-600 dark:text-black text-white px-3 py-1 rounded-md text-sm"
            >
              Add Feature
            </button>
          </div>

          {formData.features.map((feature, index) => (
            <div
              key={index}
              className="mt-4 p-3 bg-white dark:bg-background rounded-md"
            >
              <div className="flex justify-between">
                <h3 className="font-medium">Feature {index + 1}</h3>
                {formData.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  Feature Name:
                </label>
                <input
                  type="text"
                  name="title"
                  value={feature.title}
                  onChange={(e) => handleFeatureChange(index, e)}
                  placeholder="Enter feature name"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  Feature Description:
                </label>
                <input
                  type="text"
                  name="description"
                  value={feature.description}
                  onChange={(e) => handleFeatureChange(index, e)}
                  placeholder="Enter feature description"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  Feature URL:
                </label>
                <input
                  type="text"
                  name="url"
                  value={feature.url}
                  onChange={(e) => handleFeatureChange(index, e)}
                  placeholder="Enter feature URL"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-zinc-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4">
          <button
            type="submit"
            className="w-full bg-red-600 dark:bg-white dark:text-black hover:bg-red-700 text-white py-3 px-4 rounded-md font-medium"
            disabled={isLoading}
          >
            {editData.isEdit ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminAddResources;
